import React, { ReactNode } from "react";
import { View, StyleSheet, Platform } from "react-native";
import { List, MD3Theme, useTheme } from "react-native-paper";
import { mobUser } from "../../../mobx/MobUser";
import { Text } from "../../../utils/fontsLoader";
import { SafeAreaView } from "react-native-safe-area-context";

interface ParamElement {
  number: number;
  label: { fr: string };
  type?: { fr: string };
  quantity?: number;
  additionalDetails?: string;
}

interface EquipmentDisplayProps {
  title: string;
  equipment: ParamElement[];
}

/**
 * Display equipment in a comma separated list (inline intead of column for web-app CSS issue)
 * @param {ParamElement} equipment
 * @returns {ReactNode} a Text tag => equipement1, equipement2, ..., equipementN
 */
const displayWebApp = (title: string, equipmentObj: { [key: string]: ParamElement | ParamElement[] }): ReactNode => {
  const items: ReactNode[] = [];

  Object.entries(equipmentObj).forEach(([key, value]) => {
    if (key === "_trad") return;

    const elements = Array.isArray(value) ? value : [value];

    elements.forEach((element, index) => {
      const label = element._trad?.[mobUser.Language] || element.label?.fr || "Élément";
      const qty = element.quantity ?? 1;
      const details = element.translatedAdditionalDetails?.[mobUser.Language] || element.additionalDetails;

      items.push(
        <View key={`${key}-${index}`} style={{ flexDirection: "row", alignItems: "flex-start", marginBottom: 6 }}>
          <Text style={{ marginRight: 6 }}>•</Text>
          <Text style={{ flex: 1 }}>
            {qty > 1 ? `${qty} ` : ""}
            {label}
            {details ? ` ${details}` : ""}
          </Text>
        </View>
      );
    });
  });

  return (
    <View style={{
      borderColor: "#EFEFEF",
      borderWidth: 3,
      width: "80%",
      alignSelf: "center",
      borderRadius: 20,
      padding: 20,
      marginVertical: 20
    }}>
      <Text style={{ fontSize: 18, marginBottom: 10 }}>{title}</Text>
      {items.length > 0 ? items : <Text style={{ fontStyle: 'italic' }}>Aucun équipement</Text>}
    </View>
  );
};

const EquipmentDisplayCard = ({ title, equipment }: EquipmentDisplayProps) => {

  const [expanded, setExpanded] = React.useState(false);
  const handlePress = () => setExpanded(!expanded);

  const theme = useTheme();
  const styles = stylesGen(theme);

  if (Platform.OS === "web") {
    return displayWebApp(title, equipment);
  }

  return (
    <SafeAreaView style={{ flex: 1 }} edges={['bottom']}>
      <View style={styles.container}>
        <List.Section>
          <List.Accordion
            style={{
              borderRadius: 20,
              width: "95%",
              alignSelf: "center",
            }}
            title={title}
            expanded={expanded}
            onPress={handlePress}
            right={props => (
              <List.Icon {...props} color={expanded ? theme.colors.primary : "black"} icon={expanded ? "chevron-up" : "chevron-down"} />
            )}
          >
            {Object.keys(equipment).map((key) => {
              if (key === "_trad") return null;
              const element = equipment[key];
              if (Array.isArray(element)) {
                for (const e of element) {
                  return (
                    <List.Item
                      key={key}
                      style={{ paddingVertical: 0 }}
                      title={
                        "• " + (e.quantity ? e.quantity + " " : "1 ") +
                        (e._trad[mobUser.Language] ? e._trad[mobUser.Language] : "") +
                        (e.additionalDetails
                          ? " " + (e.translatedAdditionalDetails?.[mobUser.Language] || e.additionalDetails)
                          : "")
                      }
                    />)
                }
              }
              return (
                <List.Item
                  key={key}
                  style={{ paddingVertical: 0 }}
                  title={
                    "• " + (element.quantity ? element.quantity + " " : "") +
                    (element._trad[mobUser.Language] ? element._trad[mobUser.Language] : "") +
                    (element.additionalDetails
                      ? " " + (element.translatedAdditionalDetails?.[mobUser.Language] || element.additionalDetails)
                      : "")
                  }
                />
              )
            })}
          </List.Accordion>
        </List.Section>
      </View>
    </SafeAreaView>
  );
};

const stylesGen = (theme: MD3Theme) => StyleSheet.create({
  container: {
    borderColor: theme.colors.secondary,
    borderWidth: 3,
    width: "80%",
    alignSelf: "center",
    borderRadius: 20,
  },
  title: {
    fontSize: 18,
    fontFamily: "NotoSans-Bold",
    marginBottom: 10,
  },
  label: {
    marginLeft: 15,
    fontSize: 16,
    fontFamily: "NotoSans-Medium",
  },
  details: {
    fontSize: 14,
    color: "#555",
  },
});

export default EquipmentDisplayCard;